/**
 * Discount & Spread Settings — FMV spread matrix (Manage Capital's Segment 3). Treasury/SOFR
 * spreads at Credit Grade x
 * Borrower Industry x Term, governed by propose -> approve, exactly like every other PRC
 * proposal feature.
 *
 * Backend: portfolio-risk-concentrations/app/router/fair_value.py, against the real, given
 * athena_input.prc_fair_value_spread / athena_input.prc_fair_value_spread_proposals tables —
 * not the teammate's own athena_bfsi_prc.prc_fair_value_spread(_proposals), and not a
 * PRC-owned audit-log table (an earlier, now-abandoned direct-save design had one).
 *
 * ── One proposal row per grade, submitted as a batch ─────────────────────────────────────
 * The real proposals table holds exactly one new spread pair per row and requires a
 * credit_grade, so "Propose new version" — which submits every grade's SOFR and Treasury
 * spread in the matrix at once — inserts N rows sharing a batch id, decided together. See
 * fair_value.py's module docstring for why. `FvSpreadBatch` is that batch, reconstituted
 * server-side; there is no per-row proposal type on this side.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { getJson, postJson, PRC_PREFIX } from "./client";

/** The five term buckets this grid is fixed to — matches the backend's TERM_MONTHS and the
 *  athena_input.prc_fair_value_spread CHECK constraint the base table carries. */
export const TERM_MONTHS: number[] = [12, 24, 36, 48, 60];

/** Real acronyms that occur in athena_input.prc_fair_value_spread's borrower_industry values
 *  (e.g. "GPS") — kept upper case by industryLabel below rather than Title Cased into "Gps". */
const _INDUSTRY_ACRONYMS = new Set(["GPS", "SIC", "PG", "FICO", "IRR", "SOFR", "TTLV"]);

/**
 * borrower_industry is stored ALL CAPS ("TELECOMMUNICATION EQUIP.", "TEST & MEASUREMENT") —
 * a data convention, not an acronym. Title Case for display, per this app's own label style,
 * except a token that's a genuine acronym (see _INDUSTRY_ACRONYMS above), which stays as-is.
 * A token that ISN'T entirely uppercase letters (punctuation like "&"/"/", or an already
 * mixed-case value already present in the data, e.g. "Copier") is left untouched either way.
 */
export function industryLabel(raw: string): string {
  return raw
    .split(" ")
    .map((word) => {
      const letters = word.replace(/[^A-Za-z]/g, "");
      if (!letters || letters !== letters.toUpperCase() || _INDUSTRY_ACRONYMS.has(letters)) {
        return word;
      }
      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export interface FvSpreadLeaf {
  creditGrade: string;
  borrowerIndustry: string;
  termMonths: number;
  /** Read-only, live — sourced from athena_intelligence.treasury. Null only if the curve
   *  itself has no data yet for the tenor(s) this term needs. */
  treasuryRatePct: number | null;
  treasuryRateAsOf: string | null;
  /** True for the 48-month term only — there is no published 4-year Treasury series, so it's
   *  linearly interpolated between the 3Y and 5Y points. Shown so nobody mistakes it for a
   *  direct market quote. */
  treasuryRateIsInterpolated: boolean;
  treasurySpreadPct: number | null;
  /** = treasuryRatePct + treasurySpreadPct, read-only. */
  treasuryFairValuePct: number | null;
  /** Read-only, live — the SOFR overnight rate, same value on every term row (there is no
   *  term SOFR curve synced). */
  sofrRatePct: number | null;
  sofrRateAsOf: string | null;
  sofrSpreadPct: number | null;
  sofrFairValuePct: number | null;
  lastSetLevel: "CREDIT_GRADE" | "INDUSTRY" | "TERM";
  updatedAt: string | null;
}

export interface FvSpreadGrid {
  leaves: FvSpreadLeaf[];
  gradeRollups: unknown[];
  industryRollups: unknown[];
}

/** How many rows a save at this scope would touch — read before showing a wide-blast-radius
 *  confirmation. Not cached (`enabled: false`, fetched on demand via refetch). */
export function useFairValueSpreadPreview(scope: {
  scopeLevel: "CREDIT_GRADE" | "INDUSTRY" | "TERM";
  creditGrade: string;
  borrowerIndustry?: string;
  termMonths?: number;
} | null) {
  return useQuery({
    queryKey: ["prc-fair-value-spread-preview", scope],
    queryFn: () => {
      const params = new URLSearchParams({
        scopeLevel: scope!.scopeLevel,
        creditGrade: scope!.creditGrade,
      });
      if (scope!.borrowerIndustry) params.set("borrowerIndustry", scope!.borrowerIndustry);
      if (scope!.termMonths != null) params.set("termMonths", String(scope!.termMonths));
      return getJson<{ affectedRowCount: number }>(
        `${PRC_PREFIX}fair-value-spreads/preview?${params.toString()}`,
      );
    },
    enabled: false,
  });
}

/**
 * Both reads go to the real backend unconditionally — see investorListing.ts's note on why
 * no PRC hook has a mock-data fallback any more.
 */
export function useFairValueSpreads() {
  return useQuery({
    queryKey: ["prc-fair-value-spreads"],
    queryFn: () => getJson<FvSpreadGrid>(`${PRC_PREFIX}fair-value-spreads`),
  });
}

// ── Proposal batches ──────────────────────────────────────────────────────────────────────
export type FvBatchStatus = "PENDING" | "APPROVED" | "REJECTED";

/** What "Propose New Version" submits per grade — the new values only. */
export interface FvBatchGradeInput {
  creditGrade: string;
  treasurySpreadPct: number | null;
  sofrSpreadPct: number | null;
}

/**
 * One grade's before/after, as the queue and the log both need to show it. `old*` is either
 * the value live in the grid right now (a still-PENDING batch) or the snapshot taken at
 * approval (an APPROVED one) — see fair_value.py's `_member_row` docstring for why those are
 * unavoidably two different sources rather than one column.
 *
 * `leafCount` and `mixedOld` exist because of fan-out (All Industry / All Terms): a batch can
 * hold many (industry, term) leaves for the SAME grade. `new*` is always one value — one input
 * produced every fan-out row — but `old*` is null and `mixedOld` is true when those leaves
 * didn't all start at the same prior spread, rather than presenting one arbitrary leaf's prior
 * value as if it were universal.
 */
export interface FvBatchGradeDetail {
  creditGrade: string;
  oldTreasurySpreadPct: number | null;
  newTreasurySpreadPct: number | null;
  oldSofrSpreadPct: number | null;
  newSofrSpreadPct: number | null;
  leafCount: number;
  mixedOld: boolean;
}

export interface FvSpreadBatch {
  batchId: string;
  /** Null, with industryCount > 1, once a fan-out batch (All Industry) spans more than one
   *  real industry — see fair_value.py's `_batch_row`. */
  borrowerIndustry: string | null;
  industryCount: number;
  /** Null, with termCount > 1, once a fan-out batch (All Terms) spans more than one term. */
  termMonths: number | null;
  termCount: number;
  grades: FvBatchGradeDetail[];
  status: FvBatchStatus;
  reason: string | null;
  proposedByRole: string;
  proposedAt: string;
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  affectedLeafCount: number | null;
}

export interface FvSpreadBatchPage {
  batches: FvSpreadBatch[];
  total: number;
}

export function useFairValueSpreadProposals(page: { limit: number; offset: number; status?: FvBatchStatus }) {
  return useQuery({
    queryKey: ["prc-fair-value-spread-proposals", page],
    queryFn: () => {
      const params = new URLSearchParams({
        limit: String(page.limit),
        offset: String(page.offset),
      });
      if (page.status) params.set("status", page.status);
      return getJson<FvSpreadBatchPage>(
        `${PRC_PREFIX}fair-value-spread-proposals?${params.toString()}`,
      );
    },
  });
}

/** The approval queue's source — every PENDING batch, unpaginated (queues don't page). */
export function usePendingFairValueSpreadBatches() {
  return useFairValueSpreadProposals({ limit: 100, offset: 0, status: "PENDING" });
}

export interface ProposeFairValueSpreadBatchInput {
  /** Omit (or ignore) when allIndustries is true — the backend fans out to every real
   *  industry itself. */
  borrowerIndustry?: string;
  allIndustries?: boolean;
  /** Omit (or ignore) when allTerms is true — the backend fans out to every one of the five
   *  term bands itself. */
  termMonths?: number;
  allTerms?: boolean;
  reason: string;
  grades: FvBatchGradeInput[];
  proposedByRole?: string;
}

function invalidateFairValueSpreads(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["prc-fair-value-spreads"] });
  qc.invalidateQueries({ queryKey: ["prc-fair-value-spread-proposals"] });
}

export function useProposeFairValueSpreadBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProposeFairValueSpreadBatchInput) =>
      postJson<{ batchId: string; gradeCount: number; leafCount: number }>(
        `${PRC_PREFIX}fair-value-spread-proposals`,
        input,
      ),
    onSuccess: () => invalidateFairValueSpreads(qc),
  });
}

export interface FvBatchReviewResult {
  ok: boolean;
  error?: string;
  decision?: "APPROVED" | "REJECTED";
  batchId?: string;
  gradeCount?: number;
}

export function useReviewFairValueSpreadBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      batchId: string;
      decision: "APPROVED" | "REJECTED";
      notes: string;
      reviewerRole?: string;
    }) =>
      postJson<FvBatchReviewResult>(
        `${PRC_PREFIX}fair-value-spread-proposals/${encodeURIComponent(input.batchId)}/review`,
        { decision: input.decision, notes: input.notes, reviewerRole: input.reviewerRole },
      ),
    onSuccess: () => invalidateFairValueSpreads(qc),
  });
}

/** How a batch's scope reads in the queue and the log. A fan-out batch (All Industry and/or
 *  All Terms) has no single industry/term to name, so it reads as a count instead — "24
 *  Industries" rather than a specific one. */
export function batchScopeLabel(b: {
  borrowerIndustry: string | null;
  industryCount: number;
  termMonths: number | null;
  termCount: number;
  grades: FvBatchGradeDetail[];
}): string {
  const gradeList = b.grades.map((g) => g.creditGrade).join(", ");
  const industryLabel =
    b.borrowerIndustry ?? `${b.industryCount} Industr${b.industryCount === 1 ? "y" : "ies"}`;
  const termLabel = b.termMonths != null ? `${b.termMonths}mo` : `${b.termCount} Terms`;
  return `${gradeList} · ${industryLabel} · ${termLabel}`;
}
